import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { motion } from 'framer-motion';

const ModernList = ({
  items = [],
  type = 'bullet', // bullet, number, icon
  spacing = 2,
  align = 'left',
  heading,
  headingLevel = 5,
  ...props
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, x: -10 },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  // Render each item as a glassy animated card
  const renderList = (list) => (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      sx={{ display: 'flex', flexDirection: 'column', gap: spacing, textAlign: align }}
    >
      {list.map((item, idx) => {
        if (typeof item === 'string') {
          return (
            <motion.div key={idx} variants={itemVariants}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minHeight: 48,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {type === 'number' ? (
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 700, mr: 2 }}>{idx + 1}.</Typography>
                ) : null}
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{item}</Typography>
              </Paper>
            </motion.div>
          );
        } else if (typeof item === 'object' && item.text) {
          return (
            <motion.div key={idx} variants={itemVariants}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  minHeight: 48,
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {item.icon && <Box sx={{ mr: 1 }}>{item.icon}</Box>}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.text}</Typography>
                </Box>
                {item.children && Array.isArray(item.children) && (
                  <Box sx={{ mt: 1, pl: 3 }}>
                    {item.children.map((child, cidx) => (
                      <Typography key={cidx} variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{child}</Typography>
                    ))}
                  </Box>
                )}
              </Paper>
            </motion.div>
          );
        }
        return null;
      })}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', mb: spacing }} {...props}>
      {heading && (
        <Typography variant={`h${headingLevel}`} sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
          {heading}
        </Typography>
      )}
      {renderList(items)}
    </Box>
  );
};

export default ModernList;
